import { WorkerCardFormatOption } from 'containers/worker/helpers/utils';
import React from 'react';
import { SelectOptionElement } from '@odin-labs/components';
import { CardStatus, WorkerCardType } from 'apollo/generated/client-operations';
import { getSingleOrNull, typedAs } from 'utils';
import { IdCardIcon } from 'components/icons';
import {
  FormInputTypes,
  GridColSpan,
  TypedFormInputs,
  UseFormMethods,
  UseInputs,
} from 'components/form';
import { QrCodeField, QrCodeFieldProps } from 'components/qrCodeField';
import { BadgeChecklist } from 'components/badgeChecklist/BadgeChecklist';
import { validateScannedBadgeQrCode } from 'containers/utils';
import { WorkerCard } from 'containers/worker/modals/badgeModal/components/types';
import {
  CARD_WITH_SCANNABLE_NUMBER,
  getBadgeNumberValidation,
} from 'containers/worker/modals/badgeModal/utils';
import { AssignBadgeFormData } from './types';

const getFormInputs = ({
  proximityCardFormatOptions,
  allowScanning,
  validationRegex,
  validationMessage,
  encoding,
}: {
  proximityCardFormatOptions: SelectOptionElement[];
  allowScanning: boolean;
  validationRegex?: string;
  validationMessage?: string;
  encoding?: string;
}): TypedFormInputs<AssignBadgeFormData> => {
  return {
    proximityCardFormatId: {
      element: FormInputTypes.Select,
      label: 'Card format',
      elementProps: {
        disabled: (proximityCardFormatOptions?.length ?? 0) < 2,
        options: proximityCardFormatOptions ?? [],
        isClearable: false,
      },
      validation: { required: true },
      layout: GridColSpan.SpanFull,
    },
    badgeNumber: {
      element: FormInputTypes.CustomInput,
      label: 'Badge number',
      elementProps: {
        customInput: QrCodeField,
        // @ts-expect-error [it's a fine icon]
        icon: IdCardIcon,
        placeholder: allowScanning ? 'Enter or scan badge number' : null,
        ...typedAs<Omit<QrCodeFieldProps, 'name'>>({
          allowScanning,
          parseQrCode: validateScannedBadgeQrCode,
        }),
      },
      validation: getBadgeNumberValidation({
        validationRegex,
        validationMessage,
        encoding,
      }),
      layout: GridColSpan.SpanFull,
    },
    badges: {
      element: FormInputTypes.CustomInput,
      elementProps: {
        customInput: BadgeChecklist,
      },
      layout: GridColSpan.SpanFull,
    },
  };
};

export const getFormInputsHook =
  (args: {
    proximityCardFormatOptions: SelectOptionElement[];
  }): UseInputs<AssignBadgeFormData> =>
  (
    form: UseFormMethods<AssignBadgeFormData>,
  ): TypedFormInputs<AssignBadgeFormData> => {
    const { proximityCardFormatOptions } = args;
    const { watch } = form;
    const proximityCardFormatIdElement = watch('proximityCardFormatId') as
      | WorkerCardFormatOption
      | null
      | undefined;
    const allowScanning =
      proximityCardFormatIdElement?.name === CARD_WITH_SCANNABLE_NUMBER;

    React.useEffect(() => {
      if (proximityCardFormatIdElement?.name === CARD_WITH_SCANNABLE_NUMBER) {
        const badgeNumberFieldRef = form.getFieldRef('badgeNumber');
        badgeNumberFieldRef?.open?.();
      }
    }, [proximityCardFormatIdElement?.name]);

    return React.useMemo(
      () =>
        getFormInputs({
          proximityCardFormatOptions,
          allowScanning,
          validationRegex: proximityCardFormatIdElement?.validationRegex,
          validationMessage: proximityCardFormatIdElement?.validationMessage,
          encoding: proximityCardFormatIdElement?.encoding,
        }),
      [
        proximityCardFormatOptions,
        allowScanning,
        proximityCardFormatIdElement?.validationRegex,
        proximityCardFormatIdElement?.validationMessage,
        proximityCardFormatIdElement?.encoding,
      ],
    );
  };

export const getFormDefaultValues = (args: {
  workerCards: WorkerCard[];
  proximityCardFormatOptions: SelectOptionElement[];
  defaultProximityCardFormatOption: SelectOptionElement;
}): AssignBadgeFormData => {
  const {
    workerCards,
    proximityCardFormatOptions,
    defaultProximityCardFormatOption,
  } = args;

  return {
    proximityCardFormatId:
      defaultProximityCardFormatOption ??
      getSingleOrNull(proximityCardFormatOptions),
    badgeNumber: '',
    badges:
      workerCards
        ?.filter(
          ({ cardStatus, workerCardFormat }) =>
            cardStatus === CardStatus.Active &&
            workerCardFormat.cardType === WorkerCardType.Proximity,
        )
        ?.map((workerCard) => ({ workerCard, selected: true })) ?? null,
  };
};
